/**
 * The type of (redux) action which signals that server authentication has
 * becoming available or unavailable or logged in user has changed.
 *
 * {
 *     type: AUTH_STATUS_CHANGED,
 *     authEnabled: boolean,
 *     authLogin: string
 * }.
 */
export const AUTH_STATUS_CHANGED = 'AUTH_STATUS_CHANGED';

export const TOGGLE_NAVIGATION_BAR_MORE_OPTIONS = 'TOGGLE_NAVIGATION_BAR_MORE_OPTIONS';

export const TOGGLE_LAYOUT_OVERLAY = 'TOGGLE_LAYOUT_OVERLAY';

/** * The type of Redux action which sets the object-fit property for a participant's video.
 *
 * {
 *     type: SET_PARTICIPANT_OBJECT_FIT,
 *     id: string,
 *     objectFit: string
 * }
 */
export const SET_OBJECT_FIT = 'SET_OBJECT_FIT';


/**
 * The type of (redux) action which signals that a system (non-participant) message has been received.
 *
 * {
 *     type: NON_PARTICIPANT_MESSAGE_RECEIVED,
 *     id: String,
 *     json: Object
 * }.
 */
export const NON_PARTICIPANT_MESSAGE_RECEIVED = 'NON_PARTICIPANT_MESSAGE_RECEIVED';


/**
  * The type of (redux) action which signals that a specific conference failed.
  *
  * {
  *     type: CONFERENCE_FAILED,
  *     conference: MHConference,
  *     error: Error
  * }.
  */
export const CONFERENCE_FAILED = 'CONFERENCE_FAILED';

/**
  * The type of (redux) action which signals that a specific conference was
  * joined.
  *
  * {
  *     type: CONFERENCE_JOINED,
  *     conference: MHConference
  * }.
  */
export const CONFERENCE_JOINED = 'CONFERENCE_JOINED';

/**
  * The type of (redux) action which signals that a specific conference was left.
  *
  * {
  *     type: CONFERENCE_LEFT,
  *     conference: MHConference
  * }.
  */
export const CONFERENCE_LEFT = 'CONFERENCE_LEFT';

/**
  * The type of (redux) action, which indicates conference subject changes.
  *
  * {
  *     type: CONFERENCE_SUBJECT_CHANGED
  *     subject: string
  * }.
  */
export const CONFERENCE_SUBJECT_CHANGED = 'CONFERENCE_SUBJECT_CHANGED';

/**
 * The type of (redux) action, which indicates conference UTC timestamp changes.
 *
 * {
 *      type: CONFERENCE_TIMESTAMP_CHANGED
 *      timestamp: number
 * }.
 */
export const CONFERENCE_TIMESTAMP_CHANGED = 'CONFERENCE_TIMESTAMP_CHANGED';

/**
  * The type of (redux) action which signals that an uuid for a conference has been set.
  *
  * {
  *     type: CONFERENCE_UNIQUE_ID_SET,
  *     conference: MHConference
  * }.
  */
export const CONFERENCE_UNIQUE_ID_SET = 'CONFERENCE_UNIQUE_ID_SET';

/**
  * The type of (redux) action which signals that a specific conference will be
  * joined.
  *
  * {
  *     type: CONFERENCE_WILL_JOIN,
  *     conference: MHConference
  * }.
  */
export const CONFERENCE_WILL_JOIN = 'CONFERENCE_WILL_JOIN';

/**
  * The type of (redux) action which signals that a specific conference will be
  * left.
  *
  * {
  *     type: CONFERENCE_WILL_LEAVE,
  *     conference: MHConference
  * }.
  */
export const CONFERENCE_WILL_LEAVE = 'CONFERENCE_WILL_LEAVE';

/**
  * The type of (redux) action which signals that the data channel with the
  * bridge has been established.
  *
  * {
  *     type: DATA_CHANNEL_OPENED
  * }.
  */
export const DATA_CHANNEL_OPENED = 'DATA_CHANNEL_OPENED';

/**
 * The type of (redux) action which updates the current transport status for
 * the active conference.
 *
 * {
 *     type: TRANSPORT_STATE_CHANGED,
 *     transportState: Object
 * }.
 */
export const TRANSPORT_STATE_CHANGED = 'TRANSPORT_STATE_CHANGED';


/**
 * The type of (redux) action which indicates that an endpoint message
 * sent by another participant to the data channel is received.
 *
 * {
 *     type: ENDPOINT_MESSAGE_RECEIVED,
 *     participant: Object,
 *     data: Object
 * }
 */
export const ENDPOINT_MESSAGE_RECEIVED = 'ENDPOINT_MESSAGE_RECEIVED';

/**
  * Create an action for muting a remote participant.
  *
  * {
  *     type: MUTE_REMOTE_PARTICIPANT,
  *     id: string
  * }.
  */
export const FORCE_MUTE_REMOTE_PARTICIPANT = 'FORCE_MUTE_REMOTE_PARTICIPANT';

/**
  * Create an action for muting a remote participant.
  *
  * {
  *     type: ADD_FORCE_MUTE_REMOTE_PARTICIPANT,
  *     id: string
  * }.
  */
export const ADD_FORCE_MUTE_REMOTE_PARTICIPANT = 'ADD_FORCE_MUTE_REMOTE_PARTICIPANT';

/**
 * The type of (redux) action which signals that a conference will be initialized.
 *
 * {
 *     type: CONFERENCE_WILL_INIT
 * }
 */
export const CONFERENCE_WILL_INIT = 'CONFERENCE_WILL_INIT';

/**
  * Create an action for muting a remote participant.
  *
  * {
  *     type: MUTE_REMOTE_PARTICIPANT,
  *     id: string
  * }.
  */
export const FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO = 'FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO';

/**
  * Create an action for muting a remote participant.
  *
  * {
  *     type: MUTE_REMOTE_PARTICIPANT,
  *     id: string
  * }.
  */
export const GET_FORCE_MUTE_REMOTE_PARTICIPANT = 'GET_FORCE_MUTE_REMOTE_PARTICIPANT';

/**
  * Create an action for muting a remote participant.
  *
  * {
  *     type: MUTE_REMOTE_PARTICIPANT,
  *     id: string
  * }.
  */
export const GET_FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO = 'GET_FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO';

/**
  * The type of action which signals that the user has been kicked out from
  * the conference.
  *
  * {
  *     type: KICKED_OUT,
  *     conference: MHConference
  * }.
  */
export const KICKED_OUT = 'KICKED_OUT';

/**
  * The type of (redux) action which signals that the lock state of a specific
  * {@code MHConference} changed.
  *
  * {
  *     type: LOCK_STATE_CHANGED,
  *     conference: MHConference,
  *     locked: boolean
  * }.
  */
export const LOCK_STATE_CHANGED = 'LOCK_STATE_CHANGED';

/**
  * The type of (redux) action which sets the peer2peer flag for the current
  * conference.
  *
  * {
  *     type: P2P_STATUS_CHANGED,
  *     p2p: boolean
  * }.
  */
export const P2P_STATUS_CHANGED = 'P2P_STATUS_CHANGED';

/**
  * The type of (redux) action which signals to play specified touch tones.
  *
  * {
  *     type: SEND_TONES,
  *     tones: string,
  *     duration: number,
  *     pause: number
  * }.
  */
export const SEND_TONES = 'SEND_TONES';

/**
  * The type of (redux) action which updates the current known status of the
  * Follow Me feature.
  *
  * {
  *     type: SET_FOLLOW_ME,
  *     enabled: boolean
  * }.
  */
export const SET_FOLLOW_ME = 'SET_FOLLOW_ME';

/**
 * The type of (redux) action which updates the current known status of the
 * Mute Reactions Sound feature.
 *
 * {
 *     type: SET_START_REACTIONS_MUTED,
 *     enabled: boolean
 * }.
 */
export const SET_START_REACTIONS_MUTED = 'SET_START_REACTIONS_MUTED';

/**
  * The type of (redux) action which sets the password to join or lock a specific
  * {@code MHConference}.
  *
  * {
  *     type: SET_PASSWORD,
  *     conference: MHConference,
  *     method: Function
  *     password: string
  * }.
  */
export const SET_PASSWORD = 'SET_PASSWORD';

/**
  * The type of (redux) action which signals that setting a password on a
  * {@code MHConference} failed (with an error).
  *
  * {
  *     type: SET_PASSWORD_FAILED,
  *     error: string
  * }.
  */
export const SET_PASSWORD_FAILED = 'SET_PASSWORD_FAILED';

/**
  * The type of (redux) action which signals for pending subject changes.
  *
  * {
  *     type: SET_PENDING_SUBJECT_CHANGE,
  *     subject: string
  * }.
  */
export const SET_PENDING_SUBJECT_CHANGE = 'SET_PENDING_SUBJECT_CHANGE';

/**
  * The type of (redux) action which sets the current connection indicator status.
  *
  * {
  *     type: CONNECTION_INDICATOR_STATUS,
  *     status: string
  * }.
  */
export const CONNECTION_INDICATOR_STATUS = 'CONNECTION_INDICATOR_STATUS';

/**
  * The type of (redux) action which sets the name of the room of the
  * conference to be joined.
  *
  * {
  *     type: SET_ROOM,
  *     room: string
  * }.
  */
export const SET_ROOM = 'SET_ROOM';

/**
  * The type of (redux) action which updates the current known status of the
  * moderator features for starting participants as audio or video muted.
  *
  * {
  *     type: SET_START_MUTED_POLICY,
  *     startAudioMutedPolicy: boolean,
  *     startVideoMutedPolicy: boolean
  * }.
  */
export const SET_START_MUTED_POLICY = 'SET_START_MUTED_POLICY';


/**
  * The actiontype if the another moderator force mutes in the conference.
  */
export const FORCE_MUTE_REMOTE_PARTICIPANT_ID = 'FORCE_MUTE_REMOTE_PARTICIPANT_ID';

/**
  * The other moderator receives message that other moderater has force muted someone.
  *
  */
export const MODERATOR_MUTED_US_ALL = 'MODERATOR_MUTED_US_ALL';


/**
  * Mhconference joined.
  */
export const MH_CONFERENCE_JOINED = 'MH_CONFERENCE_JOINED';

/**
  * Loader for preJoin.
  */
export const LOADING_CHECKER = 'LOADING_CHECKER';


/**
  * Open Notification Boolean.
  */
export const OPEN_NOTIFICATION = 'OPEN_NOTFICATION';

/**
  * Notification details.
  */
export const NOTIFICATION_DETAILS = 'NOTIFICATION_DETAILS';

/**
  * Prejoin page for React-native.
  */
export const PREJOIN_MOBILE_DISABLED = 'PREJOIN_MOBILE_DISABLED';

/**
  * Prejoin page for React-native.
  */
export const CONFERENCE_TIME = 'CONFERENCE_TIME';


/**
 * User Joined The conference time.
 */
export const USER_JOINED_TIME = 'USER_JOINED_TIME';

/**
 * The type of (redux) action which signals that a specific conference joining is in progress.
 * A CONFERENCE_JOINED is guaranteed to follow.
 *
 * {
 *     type: CONFERENCE_JOIN_IN_PROGRESS,
 *     conference: MHConference
 * }.
 */
export const CONFERENCE_JOIN_IN_PROGRESS = 'CONFERENCE_JOIN_IN_PROGRESS';

/**
 * The type of (redux) action which signals that a specific conference call is ended.
 *
 * {
 *     type: CALL_ENDED,
 *     conferenceEnded: boolean
 * }.
 */
export const CALL_ENDED = 'CALL_ENDED';


export const MODERATOR_UNLOCK_AUDIO = 'MODERATOR_UNLOCK_AUDIO';
export const MODERATOR_UNLOCK_VIDEO = 'MODERATOR_UNLOCK_VIDEO';


export const CONFERENCE_EXPIRED = 'CONFERENCE_EXPIRED';


export const EXTEND_CONFERENCE_EXPIRY = 'EXTEND_CONFERENCE_EXPIRY';
export const IS_CUSTOM_PARTICIPANT_VISIBLE = 'IS_CUSTOM_PARTICIPANT_VISIBLE';

export const IS_CUSTOM_PARTICIPANT_EVERYONE = 'IS_CUSTOM_PARTICIPANT_EVERYONE';
